import { BookOpen, Video, MessageCircle, Award } from 'lucide-react'
import Hero from '../components/Hero'
import SEO from '../components/SEO'

export default function FoundersCorner() {
  const videos = [
    {
      title: 'Leadership in Times of Change',
      date: 'January 2024',
      description: 'Rev. Doc. Doreen shares insights on navigating leadership challenges with faith and wisdom.',
      thumbnail: '/assets/images/pexels-cottonbro-6275777.jpg'
    },
    {
      title: 'The Power of Education',
      date: 'December 2023',
      description: 'Exploring how education transforms lives and communities across Africa.',
      thumbnail: '/assets/images/kimberly-farmer-lUaaKCUANVI-unsplash.jpg'
    },
    {
      title: 'Faith and Innovation',
      date: 'November 2023',
      description: 'How to blend traditional values with modern innovation in ministry and service.',
      thumbnail: '/assets/images/susan-q-yin-2JIvboGLeho-unsplash.jpg'
    }
  ]

  const articles = [
    {
      title: 'Building Leaders Who Transform Nations',
      date: 'January 15, 2024',
      excerpt: 'Leadership is not just about position or power—it\'s about service, integrity, and the courage to make a difference...',
      category: 'Leadership'
    },
    {
      title: 'The Role of Faith in Modern Education',
      date: 'January 8, 2024',
      excerpt: 'In an increasingly secular world, faith-based education offers something unique: a holistic approach that nurtures the whole person...',
      category: 'Education'
    },
    {
      title: 'Empowering Women in Ministry',
      date: 'December 20, 2023',
      excerpt: 'God has called women to powerful roles in ministry and leadership. It\'s time we recognize and celebrate their contributions...',
      category: 'Ministry'
    },
    {
      title: 'Serving with Excellence',
      date: 'December 10, 2023',
      excerpt: 'Excellence in service is not about perfection—it\'s about giving your best, growing continuously, and honoring God in all you do...',
      category: 'Service'
    }
  ]

  return (
    <>
      <SEO 
        title="Founder's Corner - Rev. Doc. Doreen Mwendesi"
        description="Inspirational messages, video lectures, and articles from Rev. Doc. Doreen Mwendesi, founder of Rudovia Life College. Watch Doreen Speaks weekly series."
        keywords="Rev Doreen Mwendesi, Doreen Speaks, Christian leadership, ministry inspiration, theological education, women in ministry"
      />
      <div className="pt-20">
        <Hero
          title="Founder's Corner"
          subtitle="Inspiration, wisdom, and insights from Rev. Doc. Doreen Mwendesi"
          image="/assets/images/pexels-cottonbro-6275777.jpg"
          height="h-[400px]"
        />

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <img 
                  src="/assets/images/pexels-cottonbro-6275777.jpg" 
                  alt="Rev. Doc. Doreen Mwendesi" 
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div>
                <h2 className="heading-2 mb-6">Meet Rev. Doc. Doreen Mwendesi</h2>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Rev. Doc. Doreen Mwendesi is a visionary leader, educator, and servant of God with over 25 years of experience in ministry and Christian education. As the founder of Rudovia Life College, she has dedicated her life to empowering the next generation of leaders across Africa.
                </p>
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  Through her teaching, preaching, and mentorship, Rev. Doreen has impacted thousands of lives. Her passion for holistic education—combining academic excellence with spiritual formation—has shaped the vision and mission of Rudovia Life College.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Her weekly "Doreen Speaks" video series reaches audiences across the continent, offering practical wisdom on leadership, faith, education, and service.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center space-x-2 bg-secondary/10 px-4 py-2 rounded-lg">
                    <Award className="w-5 h-5 text-secondary" />
                    <span className="font-semibold text-primary">25+ Years in Ministry</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-secondary/10 px-4 py-2 rounded-lg">
                    <BookOpen className="w-5 h-5 text-secondary" />
                    <span className="font-semibold text-primary">Author & Educator</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-gradient-to-r from-primary to-blue-900 text-white">
          <div className="container-custom text-center">
            <Video className="w-16 h-16 mx-auto mb-6 text-secondary" />
            <h2 className="text-4xl md:text-5xl font-bold mb-4">#DoreenSpeaks</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Weekly video series featuring inspirational messages, leadership insights, and biblical teachings
            </p>
          </div>
        </section>

        <section className="section-padding bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Latest Video Messages</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Watch recent episodes from the Doreen Speaks series
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {videos.map((video, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
                  <div className="relative h-48 overflow-hidden group">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-primary/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Video className="w-16 h-16 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-secondary font-semibold text-sm mb-2">{video.date}</p>
                    <h3 className="text-xl font-bold text-primary mb-3">{video.title}</h3>
                    <p className="text-gray-600">{video.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary inline-flex items-center space-x-2"
              >
                <Video className="w-5 h-5" />
                <span>Watch More on YouTube</span>
              </a>
            </div>
          </div>
        </section>

        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="heading-2 mb-4">Articles & Insights</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Read thought-provoking articles on leadership, faith, education, and ministry
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {articles.map((article, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="bg-secondary text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {article.category}
                    </span>
                    <span className="text-gray-500 text-sm">{article.date}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-3">{article.title}</h3>
                  <p className="text-gray-600 mb-4">{article.excerpt}</p>
                  <button className="text-secondary hover:text-orange-600 font-semibold flex items-center space-x-2">
                    <span>Read More</span>
                    <MessageCircle className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section-padding bg-secondary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Connect with Rev. Doreen</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Follow Rev. Doc. Doreen Mwendesi on social media for daily inspiration and updates
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="https://www.facebook.com/Rudovia" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Facebook
              </a>
              <a href="https://www.linkedin.com/company/111399087/admin/dashboard/" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                LinkedIn
              </a>
            </div>
            <p className="mt-8 text-lg font-semibold">#DoreenSpeaks #RudoviaCollege</p>
          </div>
        </section>
      </div>
    </>
  )
}
